import {
  CreateSlackConfiguration,
  CreateWebhook,
  DeliveryMethod,
  SlackConfiguration,
  UpdateSlackConfiguration,
  UpdateWebhook,
  Webhook,
} from '@cast/types';
import { DEMO_CLUSTER_ID } from '@cast/utils';

import { apiObjectToKeyVal, reduceKeyVal } from 'core/forms';

import { FormModel } from './types';
import { KnownSeverities } from '../../constants';

export const makeFormModel = (
  alert?: Webhook | SlackConfiguration
): FormModel => {
  if (!alert) {
    return {
      name: '',
      callbackUrl: '',
      selectedSeverities: [...KnownSeverities],
      requestTemplate: '',
      authKeys: [{ key: '', value: '' }],
      deliveryMethod: DeliveryMethod.Slack,
      slackChannels: [],
      clusterIds: [],
      includeClusterless: true,
    };
  }

  // Check for channels property which is unique to SlackConfiguration
  // This is more robust than checking callbackUrl which could theoretically be added to SlackConfiguration
  if ('channels' in alert || 'workspaceId' in alert) {
    const slackConfiguration = alert;
    return {
      id: slackConfiguration.id,
      name: slackConfiguration.name || '',
      category: slackConfiguration.category,
      subcategory: slackConfiguration.subcategory,
      selectedSeverities: slackConfiguration.severityTriggers || [],
      deliveryMethod: DeliveryMethod.Slack,
      slackChannels: slackConfiguration.channels || [],
      slackWorkspaceId: slackConfiguration.workspaceId,
      clusterIds: slackConfiguration.clusterTriggers?.clusterIds || [],
      includeClusterless: slackConfiguration.includeClusterless ?? true,
    };
  }

  // Default to webhook if it's not a SlackConfiguration
  const webhook = alert as Webhook;
  return {
    id: webhook.id,
    name: webhook.name || '',
    callbackUrl: webhook.callbackUrl || '',
    category: webhook.category,
    subcategory: webhook.subcategory,
    selectedSeverities: webhook.severityTriggers || [],
    requestTemplate: webhook.requestTemplate || '',
    authKeys: apiObjectToKeyVal(webhook.authKeys),
    deliveryMethod: DeliveryMethod.Webhook,
    clusterIds: webhook.clusterTriggers?.clusterIds || [],
    includeClusterless: webhook.includeClusterless ?? true,
  };
};

export const makeApiModel = (
  formModel: FormModel
):
  | CreateWebhook
  | UpdateWebhook
  | CreateSlackConfiguration
  | UpdateSlackConfiguration => {
  const clusterTriggers = formModel.clusterIds?.length
    ? {
        clusterIds: formModel.clusterIds.filter((id) => id !== DEMO_CLUSTER_ID),
      }
    : undefined;

  if (formModel.deliveryMethod === DeliveryMethod.Slack) {
    const baseSlackConfig = {
      channels: formModel.slackChannels || [],
      name: formModel.name,
      severityTriggers: formModel.selectedSeverities,
      category: formModel.category,
      subcategory: formModel.category ? formModel.subcategory : undefined,
      clusterTriggers,
      includeClusterless: formModel.includeClusterless,
    };

    // If id exists, it's an update operation
    if (formModel.id) {
      return baseSlackConfig as UpdateSlackConfiguration;
    }

    // For create operation, we need workspaceId
    return {
      ...baseSlackConfig,
      workspaceId: formModel.slackWorkspaceId || '',
    } as CreateSlackConfiguration;
  }

  // Webhook configuration
  return {
    authKeys: reduceKeyVal(formModel.authKeys),
    callbackUrl: formModel.callbackUrl || '',
    name: formModel.name,
    requestTemplate: formModel.requestTemplate || '',
    severityTriggers: formModel.selectedSeverities,
    category: formModel.category,
    subcategory: formModel.category ? formModel.subcategory : undefined,
    clusterTriggers,
    includeClusterless: formModel.includeClusterless ?? false,
  };
};
